import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@/store";
import VeryTopNavbar from "@/components/common/veryTopNavbar";
import { examStatusEnu, examType } from '@/pages/dashboard/constants'
import { setExamQuestionBank, setSelectedOption } from "@/store/slices/exam/examQuestionBankSlice";

import ExamMainView from "./view"
import {
    addToResultApiCall, checkEligibilityForExamApiCall, getQuestionBankApiCall,
    updateExamStrikeApiCall, updateExamSubmitionApiCall, updateExamTimingsApiCall
} from "@/services/api";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";


export default function ExamMain() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { topSafeComponent } = useSafeAreaInsets();
    const defaultDisplayExamTime = " " + "0" + "h " + "0" + "m " + "0" + "s "
    const examQuestionBank = useSelector((state: RootState) => state.examQuestionBankReducer.examQuestionBank);

    const [isLoading, setIsLoading] = useState(true)
    const [questionBank, setQuestionBank] = useState<any>({})
    const [displayExamTimer, setDisplayExamTimer] = useState(defaultDisplayExamTime);
    const [checkEligibilityApiObj, setCheckEligibilityApiObj] = useState<any>({})
    const [examStartBtnIsLoading, setExamStartBtnIsLoading] = useState(false)
    const [examSubmitBtnIsLoading, setExamSubmitBtnIsLoading] = useState(false)
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [examStrikes, setExamStrikes] = useState(0);
    const examStrikesEventRef = useRef(false);
    const [examSubmitBtnIsLocked, setExamSubmitBtnIsLocked] = useState(true)

    const purchaseId = location.state?.purchaseId; // for test this is known as :: testExamDocId
    const courseId = location.state?.courseId;  // for test this is known as :: testExamId
    const currentExamType = location.state?.examType;

    const questionBankRef = useRef<any>(questionBank);
    const examQuestionBankRef = useRef<any>(examQuestionBank);
    const handleExamSubmitInprocessRef = useRef(false);

    // ----- START STATES : for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const totalPages = Math.ceil(examQuestionBank.length / pageSize);
    const paginatedQuestions = examQuestionBank.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );
    // ----- END STATES : for pagination



    // ----- START LOGIC : for pagination
    // useEffect(() => {
    //     if (examQuestionBank.length > 0) {
    //         setCurrentPage(1);
    //     }
    // }, [examQuestionBank]);

    const prevExamLengthRef = useRef(0);

    // testing
    // useEffect(()=>{
    //     console.log("examSubmitBtnIsLocked ", examSubmitBtnIsLocked)
    // },[examSubmitBtnIsLocked])

    useEffect(() => {
        handleExamSubmitBtnUnLock()
        if (
            prevExamLengthRef.current === 0 &&
            examQuestionBank.length > 0
        ) {
            setCurrentPage(1);
        }
        prevExamLengthRef.current = examQuestionBank.length;
    }, [examQuestionBank]);

    // Clamp currentPage if questions shrink
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);
    // ----- END LOGIC : for pagination



    useEffect(() => {
        if (purchaseId) {
            initExamPage()
        }
    }, [purchaseId])




    useEffect(() => {
        let strikeTimeout: NodeJS.Timeout | null = null;

        const handleStrike = () => {
            if (strikeTimeout) return;
            updateExamStrikeApiCallHelper()
            setExamStrikes(prev => {
                const updated = prev + 1;
                if (updated >= 4) {
                    // console.log("Too many strikes. Auto-submitting exam.", checkEligibilityApiObj.currentExamStatus);
                    if (checkEligibilityApiObj.currentExamStatus === examStatusEnu.started) {
                        handleExamSubmit();
                    }
                }
                return updated;
            });

            strikeTimeout = setTimeout(() => {
                strikeTimeout = null;
            }, 500);
        };

        const onBlur = () => handleStrike();
        const onVisibilityChange = () => {
            if (document.hidden) handleStrike();
        };


        // Only attach if exam is started and events not already bound
        if (
            checkEligibilityApiObj.currentExamStatus === examStatusEnu.started &&
            !examStrikesEventRef.current
        ) {
            examStrikesEventRef.current = true;
            window.addEventListener("blur", onBlur);
            document.addEventListener("visibilitychange", onVisibilityChange);
        }

        // Always return a cleanup function
        return () => {
            if (examStrikesEventRef.current) {
                window.removeEventListener("blur", onBlur);
                document.removeEventListener("visibilitychange", onVisibilityChange);
                examStrikesEventRef.current = false;
            }
            if (strikeTimeout) clearTimeout(strikeTimeout);
        };
    }, [checkEligibilityApiObj]);


    useEffect(() => {
        examQuestionBankRef.current = examQuestionBank
    }, [examQuestionBank])

    useEffect(() => {
        questionBankRef.current = questionBank;
        checkIfExamIsStarted()
    }, [questionBank])



    const handleExamSubmitBtnUnLock = () => {
        // console.log(examQuestionBank)
        const areAllOptionsSelected_ = areAllOptionsSelected(examQuestionBank)
        if (areAllOptionsSelected_) {
            setExamSubmitBtnIsLocked(false)
        } else {
            setExamSubmitBtnIsLocked(true)
        }
        // setExamSubmitBtnIsLocked(true)
    }

    const areAllOptionsSelected = (arr: any) => {
        return arr.every((obj: { selectedOption: null; }) => obj.selectedOption !== null);
    }


    const checkIfExamIsStarted = () => {
        // testing
        // const testing = false
        // if (testing) {
        //     const nowMs = Date.now();
        //     const mins = 30
        //     const a = nowMs + (mins * 60 * 1000);
        //     // const aa = new Date(a).toLocaleString();
        //     handleStartExam(a);
        // }


        if (checkEligibilityApiObj.currentExamStatus
            && checkEligibilityApiObj.currentExamStatus === examStatusEnu.started) {
            if (checkEligibilityApiObj.examEndAtMilli) {
                let examEndAtMilli = checkEligibilityApiObj.examEndAtMilli
                handleStartExam(examEndAtMilli);
            }
            // if (checkEligibilityApiObj.examStrike) {
            //     setExamStrikes(checkEligibilityApiObj.examStrike)
            // }
        }
    }


    const initExamPage = async () => {
        // debugger
        const checkEligibilityForExamApiCallStatus = await checkEligibilityForExamApiCall(purchaseId, currentExamType);
        // console.log(checkEligibilityForExamApiCallStatus)
        if (checkEligibilityForExamApiCallStatus.status
            && checkEligibilityForExamApiCallStatus.data) {
            const data = checkEligibilityForExamApiCallStatus.data
            if (data.currentExamStatus === examStatusEnu.started ||
                data.currentExamStatus === examStatusEnu.unlocked) {

                // console.log('todo : update exam stricks when exam page init')
                // debugger
                // setExamStrikes(2) // testing
                if (checkEligibilityForExamApiCallStatus.data.examStrike) {
                    setExamStrikes(checkEligibilityForExamApiCallStatus.data.examStrike)
                }

                // time out is for safety as we will be using examStrikes state in useEffect of CheckEligibilityApiObj
                setTimeout(() => {
                    setCheckEligibilityApiObj(checkEligibilityForExamApiCallStatus.data)
                }, 500)

                // time out is for safety as we will be using checkEligibilityApiObj state as awell as soon as questions bank is updated
                setTimeout(() => {
                    getQuestionBank();
                }, 1000)
            } else {
                alert('Exam is already submitted')
                navigate('/');
            }
        } else {
            console.log("Issue in initExamPage")
        }
    }


    const getQuestionBank = async () => {
        // debugger
        const getQuestionBankApiCallStatus = await getQuestionBankApiCall(courseId, currentExamType);
        // console.log(getQuestionBankApiCallStatus)
        if (getQuestionBankApiCallStatus.status
            && getQuestionBankApiCallStatus.data
            && getQuestionBankApiCallStatus.data.questions
            && getQuestionBankApiCallStatus.data.questions.length > 0) {
            setQuestionBank(getQuestionBankApiCallStatus.data)
            setIsLoading(false)
        } else {
            console.log("Issue in getQuestionBank");
        }
    };

    const updateExamStrikeApiCallHelper = async () => {
        let payload: any = {
            purchaseId: purchaseId
        }
        if (currentExamType === examType.practice) {
            payload = {
                testExamDocId: purchaseId
            }
        }
        const updateExamStrikeApiCallStatus = await updateExamStrikeApiCall(payload, currentExamType);
    }


    const handleStartExam = async (countDownTime = 0) => {
        setExamStartBtnIsLoading(true)
        if (questionBank && questionBank.questions
            && questionBank.questions.length > 0) {
            const dbQuestionSet = questionBank?.questions.map((question: any) => ({
                ...question,
                selectedOption: null,
            }));
            // debugger

            // Shuffle the questions if isShuffled is true
            if (questionBank?.isShuffled) {
                dbQuestionSet.sort(() => Math.random() - 0.5);
            }

            dispatch(setExamQuestionBank(dbQuestionSet));

            if (countDownTime > 0) {
                checkExamTime(countDownTime);
            } else if (questionBank) {
                // debugger
                if (questionBank.duration) {
                    // testing
                    // const sampleExamDuration = {
                    //     "hours": 0,
                    //     "minutes": 50
                    // }
                    // const payload = {
                    //     examDuration: sampleExamDuration,
                    //     purchaseId: purchaseId
                    // }

                    const convertToExamDuration_ = convertToExamDuration(questionBank.duration)
                    let payload: any = {
                        examDuration: convertToExamDuration_,
                        purchaseId: purchaseId
                    }

                    // debugger
                    if (currentExamType === examType.practice) {
                        payload = {
                            examDuration: convertToExamDuration_,
                            testExamDocId: purchaseId
                        }
                    }

                    const updateExamTimingsApiCallStatus = await updateExamTimingsApiCall(payload, currentExamType);
                    // console.log(updateExamTimingsApiCallStatus)
                    let issueInStartingExam = 0
                    if (updateExamTimingsApiCallStatus.status
                        && updateExamTimingsApiCallStatus.data) {
                        if (updateExamTimingsApiCallStatus.data.hasOwnProperty("examStrike")) {
                            setExamStrikes(updateExamTimingsApiCallStatus.data.examStrike)
                            issueInStartingExam++
                        }
                        if (updateExamTimingsApiCallStatus.data.examEndAtMilli) {
                            let examEndAtMilli = updateExamTimingsApiCallStatus.data.examEndAtMilli
                            // const nowMs = Date.now();
                            // const mins = 30
                            // const a = nowMs + (mins * 60 * 1000);
                            // const aa = new Date(a).toLocaleString();
                            checkExamTime(examEndAtMilli);
                            issueInStartingExam++
                        }
                    }

                    if (issueInStartingExam !== 2) {
                        alert('Issue in starting exam')
                        navigate('/');
                    }
                } else {
                    alert('Issue in starting exam')
                    navigate('/');
                }
            }
        }
    }

    const checkExamTime = async (countDownTime: any) => {

        // if (document.getElementById("examPageBgHolder")) {
        //     document.getElementById("examPageBgHolder").style.height = "auto";
        // }

        timerRef.current = setInterval(() => {
            let now = new Date().getTime(); // current time in miliseconds
            let remaningTime = countDownTime - now;
            let hours = Math.floor(
                (remaningTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
            );
            let minutes = Math.floor((remaningTime % (1000 * 60 * 60)) / (1000 * 60));
            let seconds = Math.floor((remaningTime % (1000 * 60)) / 1000);

            if (remaningTime < 0) {
                // Auto submit exam
                setDisplayExamTimer(defaultDisplayExamTime);
                console.log('handle auto submit')
                clearInterval(timerRef.current!);
                handleExamSubmit();
            } else {
                setDisplayExamTimer(
                    " " + hours + "h " + minutes + "m " + seconds + "s "
                );
                // setIsExamTimeRemaning(true);
            }


            if (checkEligibilityApiObj
                && checkEligibilityApiObj.currentExamStatus !== examStatusEnu.started) {
                setExamStartBtnIsLoading(false)
                setCheckEligibilityApiObj((prev: any) => ({
                    ...prev,
                    currentExamStatus: examStatusEnu.started
                }));
            }

        }, 1000);

        // if (checkEligibilityApiObj
        //     && checkEligibilityApiObj.currentExamStatus !== examStatusEnu.started) {
        //     setCheckEligibilityApiObj((prev: any) => ({
        //         ...prev,
        //         currentExamStatus: examStatusEnu.started
        //     }));
        // }
    };

    const stopExamTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };


    const handleExamSubmit = async () => {
        // console.log('testing')
        // return
        if (!handleExamSubmitInprocessRef.current) {
            // console.log('in')

            handleExamSubmitInprocessRef.current = true
            stopExamTimer()
            setExamSubmitBtnIsLoading(true)
            const questionBankRef_ = questionBankRef.current
            const examQuestionBankRef_ = examQuestionBankRef.current
            // console.log(questionBankRef_)
            // console.log(examQuestionBankRef_)
            // debugger
            if (questionBankRef_ && questionBankRef_._id) {
                let payload: any = {
                    purchaseId: purchaseId,
                    examId: questionBankRef_._id
                }

                // debugger
                if (currentExamType === examType.practice) {
                    payload = {
                        testExamDocId: purchaseId,
                        testExamId: courseId
                    }
                }

                const updateExamSubmitionApiCallStatus = await updateExamSubmitionApiCall(payload, currentExamType)
                // console.log(updateExamSubmitionApiCallStatus)
                if (updateExamSubmitionApiCallStatus.status) {
                    setCheckEligibilityApiObj((prev: any) => ({
                        ...prev,
                        currentExamStatus: examStatusEnu.submitted
                    }));

                    let payload2: any = {
                        purchaseId: purchaseId,
                        examId: questionBankRef_._id,
                        attempts: examQuestionBankRef_
                    }

                    // debugger
                    if (currentExamType === examType.practice) {
                        payload2 = {
                            testExamDocId: purchaseId,
                            testExamId: courseId,
                            attempts: examQuestionBankRef_
                        }
                    }
                    const addToResultApiCallStaust = await addToResultApiCall(payload2, currentExamType)
                    // console.log(addToResultApiCallStaust)
                }
            } else {
                alert("Issue while submitting exam")
                // console.log("Issue while submitting exam")
            }
            setExamSubmitBtnIsLoading(false)
        }
        // console.log('ig')
    }

    const handleSelectedOption = (questionId: string, option: string) => {
        // console.log(questionId, option)
        dispatch(setSelectedOption({ questionId, selectedOption: option }));
    }



    // ----------------- START Util fn
    const handleRichTextEditorCourseDiscription = (htmlStr: any, icon: any, value: any) => {
        if (htmlStr !== undefined && icon !== null) {
            let newHtmlStr = '';
            newHtmlStr = htmlStr;
            return newHtmlStr.replace(
                /<li>/g,
                `<li style="
                    background-image: url(${icon});
                    background-repeat: no-repeat;
                    background-size: 20px 20px;
                    list-style-type: none;
                    padding-left: 30px;
                    background-position: 0 5px;
                ">`
            );
        }
        return htmlStr;
    };

    const convertToExamDuration = (durationInMinutes: any) => {
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;

        return {
            hours: hours,
            minutes: minutes
        };
    }

    const handleSetCurrentPage = (page: any) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth' // Optional: for smooth scrolling
        });
        setCurrentPage(page)
    }
    // ----------------- END Util fn





    return (
        <div className="font-roboto">
            {topSafeComponent}
            <VeryTopNavbar
                showAlways={true}
            />
            <ExamMainView
                questionBank={questionBank}
                checkEligibilityApiObj={checkEligibilityApiObj}
                handleRichTextEditorCourseDiscription={handleRichTextEditorCourseDiscription}
                handleStartExam={handleStartExam}
                isLoading={isLoading}
                displayExamTimer={displayExamTimer}
                handleSelectedOption={handleSelectedOption}
                examStartBtnIsLoading={examStartBtnIsLoading}
                handleExamSubmit={handleExamSubmit}
                examSubmitBtnIsLoading={examSubmitBtnIsLoading}
                examStrikes={examStrikes}
                examSubmitBtnIsLocked={examSubmitBtnIsLocked}

                // ----- START STATES : for pagination
                examQuestionBank={paginatedQuestions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handleSetCurrentPage}
            // ----- END : for pagination
            />
        </div>
    );
}
